import type { FeatureAccessSettingsClients } from '@ms/yammer-graphql';
import queryFeatureAccessSettings from '@ms/yammer-graphql/dist/operations/queryFeatureAccessSettings.graphql';
import { reportError } from '@ms/yammer-telemetry';
import type { RequestInitiator } from '@ms/yammer-telemetry-support';

import type { GraphqlApiTreatmentRecord, ManagedApiCaching, ManagedApiLifecycle } from '../../api';
import { getGraphqlApiRequest, getTreatmentsExtensionForGraphqlBody, managedApiAction } from '../../api';
import { normalizeFeatureAccessSettings } from '../../normalization/graphql/normalizeFeatureAccessSettings';
import type { PromiseThunkAction } from '../../types';

import { getCacheKey } from './cacheKey';
import type {
  FeatureAccessSettingsClientLoadFulfilledAction,
  FeatureAccessSettingsClientLoadPendingAction,
  FeatureAccessSettingsClientLoadRejectedAction,
} from './loadFeatureAccessSettingsActions.types';
import { getFeatureAccessSettingsStatus } from './selectors';

interface FeatureAccessSettingsResponse {
  readonly data: FeatureAccessSettingsClients;
}

const loadFeatureAccessSettingsPending = (): FeatureAccessSettingsClientLoadPendingAction => ({
  type: 'FEATURE_ACCESS_SETTINGS.LOAD_PENDING',
});

const loadFeatureAccessSettingsFulfilled = (
  payload: FeatureAccessSettingsClientLoadFulfilledAction['payload']
): FeatureAccessSettingsClientLoadFulfilledAction => ({
  type: 'FEATURE_ACCESS_SETTINGS.LOAD_FULFILLED',
  payload,
});

const loadFeatureAccessSettingsRejected = (error: unknown): FeatureAccessSettingsClientLoadRejectedAction => ({
  type: 'FEATURE_ACCESS_SETTINGS.LOAD_REJECTED',
  payload: {
    error,
  },
  error: true,
});

const loadFeatureAccessSettingsRequest = (treatments: GraphqlApiTreatmentRecord[], initiator?: RequestInitiator) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'FeatureAccessSettingsClients',
      query: queryFeatureAccessSettings,
    },
    treatments,
    initiator,
  });

const loadFeatureAccessSettingsLifecycle: ManagedApiLifecycle<FeatureAccessSettingsResponse> = {
  pending: () => loadFeatureAccessSettingsPending(),
  fulfilled: (rawFeatureAccessSettings) => {
    const payload = normalizeFeatureAccessSettings(rawFeatureAccessSettings);

    return loadFeatureAccessSettingsFulfilled(payload);
  },
  rejected: (error) => loadFeatureAccessSettingsRejected(error),
};

interface LoadFeatureAccessSettingsOptions {
  readonly caching?: ManagedApiCaching;
  readonly initiator?: RequestInitiator;
}

type Load = (options?: LoadFeatureAccessSettingsOptions) => PromiseThunkAction<void>;
export const load: Load =
  ({ caching, initiator } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const status = getFeatureAccessSettingsStatus(state);
    const treatments = getTreatmentsExtensionForGraphqlBody({
      state,
      experiments: ['Stories'],
    });

    if (status === 'pending') {
      return;
    }

    const cachingWithKey = caching && {
      ...caching,
      key: getCacheKey(),
    };

    const action = managedApiAction({
      request: loadFeatureAccessSettingsRequest(treatments, initiator),
      lifecycle: loadFeatureAccessSettingsLifecycle,
      caching: cachingWithKey,
    });

    try {
      await dispatch(action);
    } catch (error: unknown) {
      reportError({
        error,
        eventProperties: {
          errorCode: 'FeatureAccessSettingsFailed',
        },
      });

      throw error;
    }
  };
